import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
} from "@mui/material";

import { Input } from "../../../components/Input";
import { SelectComponent } from "../../../components/Select";
import { CompanyProps, TabProps } from "../../../constants/Interface";
import {
  createCompany,
  getCompany,
  updateCompany,
} from "../../../apis/company";
import { MenuItemData } from "../../../types/global.types";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { SelectTag } from "../../../components/Tag/SelectTab";
import { getAll } from "../../../apis/admin";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const menuItems: MenuItemData[] = [
  { value: "day", label: "Day(s)" },
  { value: "month", label: "Month(s)" },
  { value: "year", label: "Year(s)" },
];

const isSpacexOrIndustrial =
  process.env.REACT_APP_SERVER_TYPE === "spacex" ||
  process.env.REACT_APP_SERVER_TYPE === "industrial";

const discountTiers = isSpacexOrIndustrial
  ? [
      { label: "Tier 1", range: "10–24 Units", name: "qty_discount_1" },
      { label: "Tier 2", range: "25–49 Units", name: "qty_discount_2" },
      { label: "Tier 3", range: "50+ Units", name: "qty_discount_3" },
    ]
  : [
      { label: "Tier 1", range: "5–9 Units", name: "qty_discount_1" },
      { label: "Tier 2", range: "10+ Units", name: "qty_discount_2" },
    ];

export const SettingsTab: React.FC<TabProps> = ({ value }) => {
  const [company, setCompany] = useState<CompanyProps | null>(null);
  const [tags, setTags] = useState([]);
  const [admins, setAdmins] = useState([]);

  const { showSnackBar } = useGlobalSnackBar();
  const currentUser = useSelector((state: RootState) => state.adminAuth.admin);

  const fetchAdmins = async () => {
    try {
      const companyData = await getCompany();
      setCompany(companyData?.[0]);
      setTags(
        companyData?.[0]?.to_copy_admins
          ? JSON.parse(companyData?.[0]?.to_copy_admins)
          : []
      );
      const adminsData = await getAll();
      setAdmins(adminsData.map((res: any) => ({ ...res, ID: res.id })));
    } catch (error) {
      console.error("Failed to fetch company", error);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (currentUser?.permission !== "Administrator") {
      showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
      return;
    }

    const formData = new FormData(event.target);
    const formValues = Object.fromEntries(formData.entries());
    try {
      if (!company?.ID) {
        await createCompany({
          ...formValues,
          to_copy_admins: JSON.stringify(tags),
        });
        showSnackBar("Created new settings");
      } else {
        const reFormValues = {
          ...formValues,
          ID: company.ID,
          to_copy_admins: JSON.stringify(tags),
          active_dealer_setting_number: formValues.active_dealer_setting_number || "1",
          quote_expiration_setting_number: formValues.quote_expiration_setting_number || "1",
          invitation_expiration_days: formValues.invitation_expiration_days || "7",
          // These fields have @IsNumber() on the entity — must send as numbers
          qty_discount_1: Number(formValues.qty_discount_1) || 0,
          qty_discount_2: Number(formValues.qty_discount_2) || 0,
          qty_discount_3: Number(formValues.qty_discount_3) || 0,
          sales_tax_percentage: Number(formValues.sales_tax_percentage) || 0,
        };
        await updateCompany(reFormValues);
        showSnackBar("Settings saved successfully.");
      }
      fetchAdmins();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      showSnackBar(Array.isArray(msg) ? msg.join(', ') : (msg || "Failed to save settings."), "error");
    }
  };

  return (
    <CustomTabPanel value={value} index={1}>
      <Box
        sx={{ maxWidth: 640, mx: "auto" }}
        component="form"
        onSubmit={handleSubmit}
      >
        {/* Active Dealer Settings */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Active {process.env.REACT_APP_DEALER_NAME} Settings
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content" }}>
              Mark {process.env.REACT_APP_DEALER_FULL_NAME}s as inactive after
            </Typography>
            <Input
              type="number"
              inputProps={{ min: 1, max: 999 }}
              width={80}
              defaultValue={company?.active_dealer_setting_number}
              name="active_dealer_setting_number"
            />
            <SelectComponent
              name="active_dealer_setting_date"
              menuItems={menuItems}
              width="110px"
              defaultValue={company?.active_dealer_setting_date}
            />
          </Box>
        </Paper>

        {/* Quote Expiration */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Quote Expiration
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content" }}>
              Quotes expire after
            </Typography>
            <Input
              type="number"
              inputProps={{ min: 1, max: 999 }}
              width={80}
              defaultValue={company?.quote_expiration_setting_number}
              name="quote_expiration_setting_number"
            />
            <SelectComponent
              name="quote_expiration_setting_date"
              menuItems={menuItems}
              width="110px"
              defaultValue={company?.quote_expiration_setting_date}
            />
          </Box>
        </Paper>

        {/* Invitation Expiration */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Invitation Expiration
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content" }}>
              Invitations expire after
            </Typography>
            <Input
              type="number"
              inputProps={{ min: 1, max: 365 }}
              width={80}
              defaultValue={company?.invitation_expiration_days || 7}
              name="invitation_expiration_days"
            />
            <Typography variant="body2" color="text.secondary">
              days
            </Typography>
          </Box>
        </Paper>

        {/* Quantity Discounts */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Quantity Discounts
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {discountTiers.map((tier) => (
              <Box
                key={tier.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ width: 56, flexShrink: 0 }}
                >
                  {tier.label}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: 100, flexShrink: 0 }}
                >
                  {tier.range}
                </Typography>
                <Input
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  width={70}
                  defaultValue={(company as any)?.[tier.name]}
                  name={tier.name}
                />
                <Typography variant="body2" color="text.secondary">%</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Quote Copy Recipients */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Quote Copy Recipients
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Send quote copies to the following admins:
          </Typography>
          <SelectTag tags={tags} setTags={setTags} availableTags={admins} />
        </Paper>

        {/* Invite Message */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Default {process.env.REACT_APP_DEALER_FULL_NAME} Invite Message
          </Typography>
          <Input
            label="Message"
            name="invite_message_description"
            sx={{ width: "100%" }}
            rows={5}
            defaultValue={
              company?.invite_message_description ||
              `Thank you for your interest and loyalty to BigFoot! Find the sign-up link below to gain access to our exclusive ${process.env.REACT_APP_DEALER_NAME} portal!`
            }
          />
        </Paper>

        <Box sx={{ textAlign: "right" }}>
          <Button sx={{ px: 4 }} variant="contained" type="submit">
            Save Settings
          </Button>
        </Box>
      </Box>
    </CustomTabPanel>
  );
};
